/**
 * One shape for every failure, and one place that decides what a human sees.
 *
 * Ported from `lib/core/errors/app_failure.dart`, including the lesson that
 * came with it: **a raw exception must never reach the screen.** A retailer or
 * an admin reading `permission-denied` learns nothing and cannot act.
 *
 * `retryable` carries the second lesson. In Flutter, `AppFailure implements
 * Exception` meant Riverpod's default policy retried *every* repository
 * failure ten times with backoff — a missing index looked exactly like a hang,
 * ~38 seconds of spinner before the error rendered. Anything in this console
 * that retries must consult this flag rather than retrying blindly.
 */

export type FailureKind =
  | 'network'
  | 'permission'
  | 'notFound'
  | 'conflict'
  | 'invalid'
  | 'unknown'

export class AppFailure extends Error {
  readonly kind: FailureKind
  readonly code: string
  /** True only where trying the identical call again could plausibly work. */
  readonly retryable: boolean

  /**
   * What the server actually said, kept even when `message` replaces it.
   *
   * The callables phrase their refusals for a human and those sentences carry
   * the only information that matters — "this shop still has orders", "this
   * partner is mid-delivery". Replacing them wholesale with a generic line
   * turns an actionable refusal into a shrug, so the original is preserved
   * here and `ErrorState` shows it underneath.
   */
  readonly serverMessage: string | null

  constructor(
    message: string,
    kind: FailureKind,
    code: string,
    retryable: boolean,
    serverMessage: string | null = null,
  ) {
    super(message)
    this.name = 'AppFailure'
    this.kind = kind
    this.code = code
    this.retryable = retryable
    this.serverMessage = serverMessage
  }

  /**
   * Maps anything thrown into a failure with a sentence an admin can act on.
   *
   * The codes are the union of Firestore's and the Functions SDK's, which
   * overlap but are not identical — `functions/permission-denied` and
   * `permission-denied` are the same fact from two SDKs, so both are handled.
   */
  static from(error: unknown): AppFailure {
    if (error instanceof AppFailure) return error

    const code = String(
      (error as { code?: unknown })?.code ?? 'unknown',
    ).replace(/^functions\//, '')

    const raw = (error as { message?: unknown })?.message
    // The Functions SDK prefixes its own message; strip it so what surfaces is
    // the sentence the callable wrote, not SDK framing.
    const serverMessage =
      typeof raw === 'string' && raw.length > 0
        ? raw.replace(/^(FirebaseError:\s*)?/, '').trim()
        : null

    switch (code) {
      case 'unavailable':
      case 'deadline-exceeded':
        return new AppFailure(
          'Could not reach the server. Check the connection and try again.',
          'network',
          code,
          true,
          serverMessage
        )

      case 'permission-denied':
      case 'unauthenticated':
        return new AppFailure(
          'You are not signed in as an admin, or your session has expired. ' +
            'Sign in again.',
          'permission',
          code,
          false,
          serverMessage
        )

      case 'not-found':
        return new AppFailure(
          'That record no longer exists. It may have been deleted.',
          'notFound',
          code,
          false,
          serverMessage
        )

      case 'already-exists':
      case 'aborted':
        return new AppFailure(
          'Someone else changed this at the same time. Reload and try again.',
          'conflict',
          code,
          true,
          serverMessage
        )

      case 'failed-precondition':
        // Almost always a missing composite index in this codebase — seven
        // shipped that way, because the emulator answers any query on demand.
        return new AppFailure(
          typeof serverMessage === 'string' && serverMessage.includes('index')
            ? 'This view needs a database index that has not been created yet.'
            : 'That is not possible in the current state.',
          'invalid',
          code,
          false,
          serverMessage
        )

      case 'invalid-argument':
        // The callables validate hard and phrase their own errors for a
        // human — passing the server's sentence through is better than
        // replacing it with something vaguer.
        return new AppFailure(
          typeof serverMessage === 'string' && serverMessage.length > 0
            ? serverMessage
            : 'Some of what was entered is not valid.',
          'invalid',
          code,
          false,
          serverMessage
        )

      case 'resource-exhausted':
        return new AppFailure(
          'Too many requests at once. Wait a moment and try again.',
          'network',
          code,
          true,
          serverMessage
        )

      default:
        return new AppFailure(
          'Something went wrong. If it keeps happening, note what you were ' +
            'doing and tell the developer.',
          'unknown',
          code,
          true,
          serverMessage
        )
    }
  }
}
